import { positiveCurrencyIncludingZeroValidation, postcodeValidation } from 'utils/form/validation';
import * as yup from 'yup';
import { Form } from './RepaymentStrategy';
import { Property, PropertyField, RepaymentStrategyField } from 'modules/cm/RepaymentStrategy/RepaymentStrategy';
import { Investment, InvestmentField } from 'modules/cm/RepaymentStrategy/Investment';
import { Endowment, EndowmentField } from 'modules/cm/RepaymentStrategy/Endowment';
import { dateValidation } from 'utils/date';

const propertyValidation = yup.object().shape<Property>({
  [PropertyField.Id]: yup.number(),
  [PropertyField.FlatNumber]: yup.string().nullable(),
  [PropertyField.BuildingNumber]: yup.string().nullable(),
  [PropertyField.BuildingName]: yup.string().nullable(),
  [PropertyField.Street]: yup.string().nullable(),
  [PropertyField.City]: yup.string().nullable(),
  [PropertyField.County]: yup.string().nullable(),
  [PropertyField.CurrentMortgageBalance]: positiveCurrencyIncludingZeroValidation().nullable(),
  [PropertyField.PurchasePrice]: positiveCurrencyIncludingZeroValidation().nullable(),
  [PropertyField.DateOfPurchase]: dateValidation().nullable(),
  [PropertyField.PostCode]: postcodeValidation().nullable(),
});

const endowmentValidation = yup.object().shape<Endowment>({
  [EndowmentField.Id]: yup.number(),
  [EndowmentField.CurrentFundValue]: positiveCurrencyIncludingZeroValidation().nullable(),
  [EndowmentField.ExpectedMaturityDate]: dateValidation().nullable(),
  [EndowmentField.MonthlyContribution]: positiveCurrencyIncludingZeroValidation().nullable(),
  [EndowmentField.Provider]: yup.string().nullable(),
});

const investmentValidation = yup.object().shape<Investment>({
  [InvestmentField.Id]: yup.number(),
  [InvestmentField.CurrentFundValue]: positiveCurrencyIncludingZeroValidation().nullable(),
  [InvestmentField.ExpectedMaturityDate]: dateValidation().nullable(),
  [InvestmentField.MonthlyContribution]: positiveCurrencyIncludingZeroValidation().nullable(),
  [InvestmentField.Provider]: yup.string().nullable(),
});

const validationSchema = () => {
  return yup.object().shape<Form>({
    [RepaymentStrategyField.HaveEndownments]: yup.boolean().nullable(),
    [RepaymentStrategyField.HaveInvestments]: yup.boolean().nullable(),
    [RepaymentStrategyField.HaveOther]: yup.boolean().nullable(),
    [RepaymentStrategyField.HaveSaleOfAnotherProperty]: yup.boolean().nullable(),
    [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: yup.boolean().nullable(),

    [RepaymentStrategyField.RepaymentValueOfHaveSaleOfAnotherProperty]: positiveCurrencyIncludingZeroValidation().when(
      RepaymentStrategyField.HaveSaleOfAnotherProperty,
      {
        is: true,
        then: yup.number().required('This field is required when this section is active.'),
      },
    ),
    [RepaymentStrategyField.RepaymentValueOfSaleOfPropertyAndDownsizing]: positiveCurrencyIncludingZeroValidation().when(
      RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing,
      {
        is: true,
        then: yup.number().required('This field is required when this section is active.'),
      },
    ),
    [RepaymentStrategyField.RepaymentValueOfHaveEndownments]: positiveCurrencyIncludingZeroValidation().when(
      RepaymentStrategyField.HaveEndownments,
      {
        is: true,
        then: yup.number().required('This field is required when this section is active.'),
      },
    ),
    [RepaymentStrategyField.RepaymentValueOfHaveInvestments]: positiveCurrencyIncludingZeroValidation().when(
      RepaymentStrategyField.HaveInvestments,
      {
        is: true,
        then: yup.number().required('This field is required when this section is active.'),
      },
    ),
    [RepaymentStrategyField.RepaymentValueOfHaveOther]: positiveCurrencyIncludingZeroValidation().when(
      RepaymentStrategyField.HaveOther,
      {
        is: true,
        then: yup.number().required('This field is required when this section is active.'),
      },
    ),

    [RepaymentStrategyField.DetailsForOther]: yup
      .string()
      .nullable()
      .when(RepaymentStrategyField.HaveOther, {
        is: true,
        then: yup.string().required('This field is required when this section is active.'),
      }),

    [RepaymentStrategyField.RepaymentValueTotalCalculated]: positiveCurrencyIncludingZeroValidation()
      .required('This field is required.')
      .test(
        'Calculated total repayment matches the Total to repay',
        'The total repayment strategy provided is lower than the total amount to be repaid. This must be corrected to proceed with the case',
        function(repaymentStrategyTotal: number) {
          const totalAmountToBeRepaid = this.parent[RepaymentStrategyField.TotalAmountToBeRepaid];

          return !(totalAmountToBeRepaid > repaymentStrategyTotal);
        },
      ),
    [RepaymentStrategyField.TotalAmountToBeRepaid]: yup.number(),

    properties: yup.array().of(propertyValidation),
    endowments: yup.array().of(endowmentValidation),
    investments: yup.array().of(investmentValidation),
  });
};

export default validationSchema;
